import { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { createResultScraperService } from '../../services/resultScraper';
import type { Fight, FightResult } from '../../types';

interface ResultScraperProps {
  fight: Fight;
  onResultScraped: (result: FightResult) => void;
}

/**
 * Result Scraper Component
 * Admin component for scraping fight results from external sources
 */
export function ResultScraper({ fight, onResultScraped }: ResultScraperProps) {
  const [scraping, setScraping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const resultScraperService = createResultScraperService();

  const handleScrapeResult = async () => {
    if (!resultScraperService.hasApiKey()) {
      setError('Result scraping API key not configured. Please use manual entry as fallback.');
      return;
    }

    setScraping(true);
    setError(null);
    setSuccess(null);

    try {
      // Scrape result for this fight
      const scrapedResult = await resultScraperService.scrapeFightResult(fight);

      if (!scrapedResult) {
        setError('No result found in API. Please use manual entry as fallback.');
        setScraping(false);
        return;
      }

      // Validate result
      if (!resultScraperService.validateResult(scrapedResult, fight)) {
        setError('Scraped result does not match fight. Please verify and use manual entry.');
        setScraping(false);
        return;
      }

      // Convert to FightResult format
      const fightResult = resultScraperService.convertToFightResult(scrapedResult, fight);

      if (!fightResult) {
        setError('Could not convert scraped result. Please use manual entry.');
        setScraping(false);
        return;
      }

      // Callback with result
      onResultScraped(fightResult);
      setSuccess('Result scraped successfully! Please review and confirm.');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to scrape result';
      setError(`Error scraping result: ${errorMessage}. Please use manual entry as fallback.`);
    } finally {
      setScraping(false);
    }
  };

  return (
    <Card padding="md" className="mb-4">
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-primary-text mb-2">Scrape Result</h3>
          <p className="text-sm text-secondary-text">
            Fetch result from external source (if available)
          </p>
        </div>

        {!resultScraperService.hasApiKey() && (
          <div className="p-3 bg-warning-yellow/10 border border-warning-yellow rounded-lg">
            <p className="text-sm text-warning-yellow">
              ⚠️ Result scraping API not configured. Manual entry is always available as fallback.
            </p>
            <p className="text-xs text-secondary-text mt-1">
              Result scraping requires integration with an external API (e.g., ESPN, UFC API).
            </p>
          </div>
        )}

        <Button
          variant="secondary"
          onClick={handleScrapeResult}
          disabled={scraping || !resultScraperService.hasApiKey()}
          fullWidth
        >
          {scraping ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              Scraping Result...
            </>
          ) : (
            'Scrape Result from API'
          )}
        </Button>

        {error && (
          <div className="p-3 bg-error/10 border border-error rounded-lg">
            <p className="text-sm text-error">{error}</p>
            <p className="text-xs text-secondary-text mt-1">
              You can always enter the result manually below.
            </p>
          </div>
        )}

        {success && (
          <div className="p-3 bg-success-green/10 border border-success-green rounded-lg">
            <p className="text-sm text-success-green">{success}</p>
          </div>
        )}
      </div>
    </Card>
  );
}

